/**
 * CSV builder for the Admin Task > School List export — one row per school:
 *
 *   school_id, school_name, school_en_name, school_key, area, school_type,
 *   Matched subscription 1, Matched subscription 2, ...
 *
 * Rows are the school rows the list itself renders (`get_filtered_school_list`
 * or `get_schools_by_subscription`), so the export carries whatever filter is
 * on screen — search text, school type, and the subscription filter in either
 * of its two forms. The caller is responsible for handing over the WHOLE
 * filtered set rather than the page on screen; the three searches know
 * different things about their own size (see schoolResultCount.js).
 *
 * MATCHED SUBSCRIPTION COLUMNS mirror the table's keyword-mode column: a school
 * can hold several grants in the same family (the 2024 Pri/Sec pair, or a 2024
 * grant plus a 26/27 one), so each match gets its OWN column — the same shape
 * as extraLocaleCsv's name columns, and for the same reason: a pipe-joined
 * single cell crams a list into one Excel column that then cannot be filtered.
 * The header carries as many numbered columns as the widest row needs, and
 * always at least one, so an unfiltered export still has the documented header
 * shape (its cells are simply blank — those rows matched no subscription
 * because no subscription was asked for).
 *
 * EMPTY IS EMPTY, not '-'. The table prints a dash for a missing name because a
 * blank table cell reads as a rendering fault; a CSV cell is data, and school
 * 935 really does have `name = ''` — writing '-' there would invent a value
 * that no downstream match or import could tell from a real one.
 *
 * Pure string-in/string-out so it is unit-testable; the caller prepends the
 * UTF-8 BOM when handing the string to a Blob (Excel reads un-BOM'd UTF-8 CSV
 * as its locale codepage and mangles Chinese) — AdminTaskDialog.downloadCsvFile
 * does that for every export.
 */

import { csvField } from "./extraLocaleCsv.js";

export const SCHOOL_CSV_FIXED_HEADERS = [
  "school_id",
  "school_name",
  "school_en_name",
  "school_key",
  "area",
  "school_type",
];

export const SCHOOL_CSV_MATCHED_HEADER = "Matched subscription";

/**
 * The school_type catalogue, single-sourced here because two places need it and
 * they must not drift: the School List's type filter (a Mantine Select) and
 * this export.
 *
 * The export writes the LABEL, not the raw integer. The table's own type column
 * uses abbreviations ('Pri', 'Sec', 'Uni/Gov'), which are legible next to a
 * header and a tooltip but not in a spreadsheet a week later; these are the
 * full names the filter dropdown already shows.
 */
export const SCHOOL_TYPE_OPTIONS = [
  { value: 0, label: "Unclassified" },
  { value: 1, label: "Primary School" },
  { value: 2, label: "Secondary School" },
  { value: 3, label: "University / Post-secondary / Gov." },
  { value: 4, label: "Special School" },
  { value: 5, label: "Management Organization" },
  { value: 6, label: "Commercial Organization" },
];

/**
 * Label for a school_type value: "" when the row carries no type at all, the
 * catalogue label when it is one of the known types, and otherwise the raw
 * value as text. That last case is deliberate — the table renders an unknown
 * type as '-' and loses it, but an export that silently drops a code the
 * database is holding is how a new school type goes unnoticed.
 */
export function schoolTypeLabel(value) {
  if (value == null || value === "") {
    return "";
  }
  const code = parseInt(value, 10);
  if (!Number.isFinite(code)) {
    return String(value);
  }
  const known = SCHOOL_TYPE_OPTIONS.find((option) => option.value === code);
  return known ? known.label : String(code);
}

/** The subscription labels a row matched, as an array of plain strings. */
export function matchedSubscriptionCells(row) {
  const matched = row && row.matched_subscriptions;
  if (!Array.isArray(matched)) {
    return [];
  }
  return matched.map((label) => String(label == null ? "" : label));
}

export function buildSchoolListCsv(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const cellsPerRow = list.map((row) => matchedSubscriptionCells(row));
  const matchedColumns = Math.max(1, ...cellsPerRow.map((cells) => cells.length));
  const headers = SCHOOL_CSV_FIXED_HEADERS.concat(
    Array.from({ length: matchedColumns },
      (_, i) => `${SCHOOL_CSV_MATCHED_HEADER} ${i + 1}`)
  );
  const lines = [headers.map(csvField).join(",")];
  list.forEach((row, rowIndex) => {
    const source = row || {};
    const matched = cellsPerRow[rowIndex];
    const cells = [
      // Every cell goes through csvField: school names carry commas and
      // quotes, and `key` is admin free text, so any of them can also start
      // with a character Excel would execute as a formula.
      csvField(source.id),
      csvField(source.name),
      csvField(source.name_eng),
      csvField(source.key),
      // `area` is the school's LOCALE column — the table heads it 地區.
      csvField(source.locale),
      csvField(schoolTypeLabel(source.school_type)),
    ];
    for (let i = 0; i < matchedColumns; i += 1) {
      cells.push(csvField(matched[i] != null ? matched[i] : ""));
    }
    lines.push(cells.join(","));
  });
  // CRLF: the joiner most spreadsheet importers assume for CSV.
  return lines.join("\r\n") + "\r\n";
}

/** `school_list_20260904.csv` — dated, because these exports get compared. */
export function schoolListCsvFileName(now = new Date()) {
  const stamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('');
  return `school_list_${stamp}.csv`;
}

export default buildSchoolListCsv;
